import React from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ConfirmationDialogProps } from '@ameroservices-platform/shared/ui-components/dialog/ConfirmationDialogProps';
import errorTranslations from './errorTranslations';

interface DialogApi {
	displayDialog: (opts: Partial<ConfirmationDialogProps>) => Promise<boolean>;
}

let dialogApi: DialogApi | null = null;

export function registerDialogApi(api: DialogApi | null) {
	dialogApi = api;
}

function createDialogOptions(overrides: Partial<ConfirmationDialogProps> = {}): Partial<ConfirmationDialogProps> {
	return {
		fullWidth: true,
		simple: true,
		...overrides
	};
}

/**
 * Error dialog (styled) OK-only. Returns void.
 */
export async function displayError(
	messageOrCode: string,
	overrides: Partial<ConfirmationDialogProps> = {}
): Promise<void> {
	if (dialogApi) {
		const options = createDialogOptions({
			title: 'Fejl',
			content: (
				<Stack direction="row" spacing={1.5} alignItems="flex-start">
					<ErrorOutlineIcon color="error" sx={{ mt: '2px' }} />
					<Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
						{errorTranslations[messageOrCode] || messageOrCode}
					</Typography>
				</Stack>
			),
			buttons: [{ label: 'OK', color: 'error' }],
			...overrides
		});
		await dialogApi.displayDialog(options);
		return;
	}
	window.alert(errorTranslations[messageOrCode] || messageOrCode);
}

/**
 * OK-only informational alert (no cancel). Returns void.
 */
export async function displayAlert(message: string, overrides: Partial<ConfirmationDialogProps> = {}): Promise<void> {
	if (dialogApi) {
		const options = createDialogOptions({
			title: 'Advarsel',
			content: (
				<Stack direction="row" spacing={1.5} alignItems="flex-start">
					<ErrorOutlineIcon color="primary" sx={{ mt: '2px' }} />
					<Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
						{message}
					</Typography>
				</Stack>
			),
			buttons: [{ label: 'OK', color: 'primary' }],
			...overrides
		});
		await dialogApi.displayDialog(options);
		return;
	}
	window.alert(message);
}

/**
 * General confirm. Returns true/false.
 */
export async function displayConfirm(
	title: string,
	message: string,
	overrides: Partial<ConfirmationDialogProps> = {}
): Promise<boolean> {
	if (dialogApi) {
		const options = createDialogOptions({
			title: title,
			content: (
				<Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
					{message}
				</Typography>
			),
			buttons: [
				{ label: 'OK', color: 'primary', result: true },
				{ label: 'Annuller', color: 'primary', result: false }
			],
			disableEscapeKeyDown: true,
			disableBackdropClick: true,
			...overrides
		});
		return await dialogApi.displayDialog(options);
	}
	return window.confirm(title + '\r\n\r\n' + message);
}

/**
 * Generic dialog display function. Returns a promise resolving to the dialog result.
 */
export async function displayDialog(options: Partial<ConfirmationDialogProps>): Promise<any> {
	return dialogApi?.displayDialog(options);
}
